import {
  PayrollFileColumnTransformation,
  PayrollFileValueTransformation,
  PayrollFileRow,
  PayrollFileColumnTransformationColumn,
  PayrollFileColumnMapping,
} from '@api/lib/payrollProcessing/types';
import {
  ExtractedColumn,
  ExtractedFile,
  Mapping,
  MappingSuggestion,
  TargetFieldInfo,
} from '@client/pages/payroll/types';
import {PayrollTargetField} from '@api/lib/payrollProcessing/constants';
import mexp from 'math-expression-evaluator';
import {
  calculateTransformationPreview,
  calculateTransformationPreviewFromExtracted,
} from '@client/pages/payroll/utils/previewUtils';

/**
 * Converts accounting-format negatives like (100.00) or ($100.00) to standard negative format (-100.00).
 * Returns the original string if not in accounting format.
 */
export function convertAccountingFormat(value: string): string {
  if (!value) return value;
  const trimmed = value.trim();
  if (/^\(.*\)$/.test(trimmed)) {
    const inner = trimmed.slice(1, -1).trim();
    if (/\d/.test(inner)) {
      return '-' + inner;
    }
  }
  return value;
}

/**
 * Apply column transformation to get the raw value from source data
 */
export function getColumnValueWithFallback(
  fileRowsMap: Record<string, PayrollFileRow[]>,
  column: PayrollFileColumnTransformationColumn,
): string {
  const primaryRows = fileRowsMap[column.fileId];
  const primaryValue =
    (primaryRows && primaryRows.length > 0 && primaryRows[0] && primaryRows[0][column.name]) || '';
  if (String(primaryValue ?? '').trim() !== '') {
    return String(primaryValue ?? '');
  }

  for (const fallback of column.fallbackColumns || []) {
    const fallbackRows = fileRowsMap[fallback.fileId];
    const fallbackValue =
      (fallbackRows &&
        fallbackRows.length > 0 &&
        fallbackRows[0] &&
        fallbackRows[0][fallback.name]) ||
      '';
    if (String(fallbackValue ?? '').trim() !== '') {
      return String(fallbackValue ?? '');
    }
  }
  return '';
}

export function applyColumnTransformation(
  transformation: PayrollFileColumnTransformation,
  fileRowsMap: Record<string, PayrollFileRow[]>,
): string {
  switch (transformation.type) {
    case 'NONE': {
      if (!transformation.column) return '';
      return getColumnValueWithFallback(fileRowsMap, transformation.column);
    }
    case 'COLUMN_FALLBACK': {
      const values = (transformation.columns || []).map((col) => {
        return getColumnValueWithFallback(fileRowsMap, col);
      });
      const firstNonEmpty = values.find(
        (v) => String(v ?? '').trim() !== '' && !isBlankLikeValue(String(v ?? '')),
      );
      return firstNonEmpty ?? '';
    }
    case 'COLUMN_FORMULA': {
      const values = (transformation.columns || []).map((col) => {
        const raw = getColumnValueWithFallback(fileRowsMap, col);
        const num = parseFloat(convertAccountingFormat(raw).replace(/[^0-9.-]/g, ''));
        return isNaN(num) || !isFinite(num) ? 0 : num;
      });
      const out = evalTransformationFormula(transformation.formula, values);
      return Number.isFinite(out) ? String(out) : '0';
    }
    case 'COLUMN_TEXT_CONCATENATION': {
      return transformation.columns
        .map((col) => {
          return getColumnValueWithFallback(fileRowsMap, col);
        })
        .join(' ');
    }
    case 'COLUMN_ADDITION': {
      if (!transformation.columns || transformation.columns.length === 0) return '0';
      const sum = transformation.columns.reduce((sum, col) => {
        const raw = getColumnValueWithFallback(fileRowsMap, col);
        const value = parseFloat(
          convertAccountingFormat(String(raw || '0')).replace(/[^0-9.-]/g, ''),
        );
        return sum + (isNaN(value) || !isFinite(value) ? 0 : value);
      }, 0);
      return isFinite(sum) ? sum.toString() : '0';
    }
    case 'COLUMN_SUBTRACTION': {
      if (!transformation.columns || transformation.columns.length < 2) return '0';
      const values = transformation.columns.map((col) => {
        const raw = getColumnValueWithFallback(fileRowsMap, col);
        const value = parseFloat(
          convertAccountingFormat(String(raw || '0')).replace(/[^0-9.-]/g, ''),
        );
        return isNaN(value) || !isFinite(value) ? 0 : value;
      });
      const result = values.slice(1).reduce((acc, val) => acc - val, values[0]);
      return isFinite(result) ? result.toString() : '0';
    }
    case 'COLUMN_MULTIPLICATION': {
      if (!transformation.columns || transformation.columns.length === 0) return '0';
      const product = transformation.columns.reduce((prod, col) => {
        const raw = getColumnValueWithFallback(fileRowsMap, col);
        const value = parseFloat(
          convertAccountingFormat(String(raw || '0')).replace(/[^0-9.-]/g, ''),
        );
        return prod * (isNaN(value) || !isFinite(value) ? 1 : value);
      }, 1);
      return isFinite(product) ? product.toString() : '0';
    }
    case 'COLUMN_DIVISION': {
      if (!transformation.columns || transformation.columns.length < 2) return '0';
      const values = transformation.columns.map((col) => {
        const raw = getColumnValueWithFallback(fileRowsMap, col);
        const value = parseFloat(
          convertAccountingFormat(String(raw || '0')).replace(/[^0-9.-]/g, ''),
        );
        return isNaN(value) || !isFinite(value) ? 0 : value;
      });
      const result = values.slice(1).reduce((acc, val) => (val === 0 ? NaN : acc / val), values[0]);
      return isFinite(result) ? result.toString() : '0';
    }
    case 'COLUMN_AVERAGE': {
      if (!transformation.columns || transformation.columns.length === 0) return '0';
      const values = transformation.columns
        .map((col) => {
          const raw = getColumnValueWithFallback(fileRowsMap, col);
          const value = parseFloat(
            convertAccountingFormat(String(raw || '0')).replace(/[^0-9.-]/g, ''),
          );
          return isNaN(value) || !isFinite(value) ? 0 : value;
        })
        .filter((v) => v !== 0);
      if (values.length === 0) return '0';
      const average = values.reduce((a, b) => a + b, 0) / values.length;
      return isFinite(average) ? average.toString() : '0';
    }
    case 'COLUMN_MAXIMUM': {
      if (!transformation.columns || transformation.columns.length === 0) return '0';
      const values = transformation.columns.map((col) => {
        const raw = getColumnValueWithFallback(fileRowsMap, col);
        const value = parseFloat(
          convertAccountingFormat(String(raw || '0')).replace(/[^0-9.-]/g, ''),
        );
        return isNaN(value) || !isFinite(value) ? 0 : value;
      });
      if (values.length === 0) return '0';
      const max = Math.max(...values);
      return isFinite(max) ? max.toString() : '0';
    }
    case 'COLUMN_MINIMUM': {
      if (!transformation.columns || transformation.columns.length === 0) return '0';
      const values = transformation.columns.map((col) => {
        const raw = getColumnValueWithFallback(fileRowsMap, col);
        const value = parseFloat(
          convertAccountingFormat(String(raw || '0')).replace(/[^0-9.-]/g, ''),
        );
        return isNaN(value) || !isFinite(value) ? 0 : value;
      });
      if (values.length === 0) return '0';
      const min = Math.min(...values);
      return isFinite(min) ? min.toString() : '0';
    }
    case 'PIVOT_COLUMN': {
      // Pivot column values are pre-computed and stored in the file rows as virtual columns
      // For preview purposes, we return a sample value if available
      const pivotCol = transformation.pivotColumn;
      const rows = fileRowsMap[pivotCol.fileId];
      if (rows && rows.length > 0 && rows[0]) {
        // Try to get the value from the virtual pivot column
        const value = rows[0][pivotCol.name];
        if (value !== undefined && value !== null) {
          return String(value);
        }
      }
      // Return empty or placeholder for preview when pivot data isn't available
      return '';
    }
    case 'STATIC_VALUE': {
      return transformation.staticValue;
    }
    default:
      const _exhaustiveCheck: never = transformation;
      return '';
  }
}

/**
 * Apply value transformations to a string value
 */
export function applyValueTransformations(
  value: string,
  transformations: PayrollFileValueTransformation[],
): string {
  let result = value;

  for (const transformation of transformations) {
    result = applyValueTransformation(result, transformation);
  }

  return result;
}

/**
 * Determines if a value is "blank-like" - values that look like placeholders for empty data
 * "N/A", "00/00/0000", "0000-00-00", whitespace-only strings, "-", "n/a", etc.
 */
export function isBlankLikeValue(value: string): boolean {
  if (value == null) return true;

  const trimmed = String(value).trim();
  if (trimmed === '') return true;

  const lower = trimmed.toLowerCase();

  // Common "not applicable" variants
  const naVariants = [
    'n/a',
    'na',
    'n\\a',
    '(na)',
    '(none)',
    'none',
    'null',
    'nil',
    '-',
    '--',
    '---',
    '(not applicable)',
    '(empty)',
    'empty',
    'blank',
    '(blank)',
  ];
  if (naVariants.includes(lower)) return true;

  // Date placeholders: 00/00/0000, 0000-00-00, 00-00-0000, etc.
  const datePatterns = [
    /^0{1,4}[\/\-]0{1,2}[\/\-]0{1,4}$/, // 00/00/0000, 0000-00-00
    /^0{1,2}[\/\-]0{1,2}[\/\-]0{1,4}$/, // 00/00/00, 00-00-0000
  ];
  if (datePatterns.some((pattern) => pattern.test(trimmed))) return true;

  return false;
}

/**
 * Apply a single value transformation
 */
export function applyValueTransformation(
  value: string,
  transformation: PayrollFileValueTransformation,
): string {
  switch (transformation.type) {
    case 'NONE': {
      return value;
    }
    case 'TRIM': {
      return value.replace(/\s+/g, '');
    }
    case 'TO_UPPERCASE': {
      return value.toUpperCase();
    }
    case 'TO_LOWERCASE': {
      return value.toLowerCase();
    }
    case 'REMOVE_NON_NUMERIC': {
      return value.replace(/[^0-9]/g, '');
    }
    case 'REMOVE_NON_ALPHABETIC': {
      return value.replace(/[^a-zA-Z]/g, '');
    }
    case 'PARSE_NUMBER': {
      if (!value) return '';
      const numericValue = parseFloat(convertAccountingFormat(value).replace(/[^0-9.-]/g, ''));
      if (isNaN(numericValue) || !isFinite(numericValue)) {
        return '';
      }
      return numericValue.toString();
    }
    case 'VALUE_MAPPING': {
      const mapping = transformation.mappings.find((m) => m.from === value);
      return mapping ? mapping.to : value;
    }
    case 'DEFAULT_IF_EMPTY': {
      const isEmpty = String(value ?? '').replace(/\s+/g, '') === '';
      return isEmpty ? transformation.defaultValue : value;
    }
    case 'DEFAULT_UNLESS_VALUE': {
      return value !== transformation.unlessValue ? transformation.defaultValue : value;
    }
    case 'CONVERT_BLANK_TO_EMPTY': {
      return isBlankLikeValue(value) ? '' : value;
    }
    case 'PAD_STRING': {
      if (value.length < transformation.targetLength) {
        const padCount = transformation.targetLength - value.length;
        const padding = transformation.padCharacter.repeat(padCount);
        return transformation.padPosition === 'START' ? padding + value : value + padding;
      }
      return value;
    }
    case 'SUBSTRING': {
      if (value.length === 0) return value;
      let start: number;
      if (transformation.startIndex > 0) {
        // Positive: 1-based index from start
        start = transformation.startIndex - 1;
      } else {
        // Negative: count from end (-1 = last char)
        start = value.length + transformation.startIndex;
      }
      // Clamp start to valid range
      start = Math.max(0, Math.min(start, value.length));
      const len = transformation.length ?? value.length - start;
      return value.substring(start, start + len);
    }
    case 'SPLIT_AND_EXTRACT': {
      if (value.length === 0) return value;
      const parts = value.split(transformation.delimiter);
      if (parts.length === 0) return value;
      let partIdx: number;
      if (transformation.partIndex > 0) {
        // Positive: 1-based index from start
        partIdx = transformation.partIndex - 1;
      } else {
        // Negative: count from end (-1 = last part)
        partIdx = parts.length + transformation.partIndex;
      }
      // Check if index is valid
      if (partIdx >= 0 && partIdx < parts.length) {
        return parts[partIdx].trim();
      }
      return '';
    }
    default: {
      const _exhaustiveCheck: never = transformation;
      return value;
    }
  }
}

export function evalTransformationFormula(formula: string, values: number[]): number {
  if (!formula) {
    return NaN;
  }
  // Replace $1, $2, ... with corresponding numeric literals (fallback to 0)
  const expr = formula.replace(/\$(\d+)/g, (_, idx: string) => {
    const v = values[Number(idx) - 1];
    const num = typeof v === 'number' && Number.isFinite(v) ? v : 0;
    return String(num);
  });

  // Allowlist: numbers, operators, parentheses, decimal points, and whitespace
  if (!/^[-+*/^().\d\s]+$/.test(expr)) {
    return NaN;
  }

  try {
    const Mexp = new mexp();
    const result = Mexp.eval(expr);
    return typeof result === 'number' && Number.isFinite(result) ? result : NaN;
  } catch {
    return NaN;
  }
}

export function applySuggestionsToMappings(
  columnsByFileId: Record<string, ExtractedColumn[]>,
  targetFields: TargetFieldInfo[],
  mappings: PayrollFileColumnMapping[],
  suggestions: Record<string, MappingSuggestion | undefined>,
) {
  const updatedMappings = [...mappings];
  for (const tf of targetFields) {
    const sug = suggestions[tf.field];
    if (!sug) continue;
    const column = columnsByFileId[sug.fileId]?.find((c) => c.index === sug.index);
    if (!column) continue;
    const idx = updatedMappings.findIndex((m) => m.targetField === tf.field);

    const mapping: PayrollFileColumnMapping = {
      targetField: tf.field,
      columnTransformation: {
        type: 'NONE',
        column: {name: column.name, index: column.index, fileId: sug.fileId},
      },
      valueTransformations: [],
      exampleValuePreTransformation: column.samples[0] || '',
      exampleValuePostTransformation: column.samples[0] || '',
    };

    const preview = calculateTransformationPreview(mapping, columnsByFileId);
    const mappingWithPreview = {
      ...mapping,
      exampleValuePreTransformation: preview.pre,
      exampleValuePostTransformation: preview.post,
    };

    if (idx >= 0) {
      updatedMappings[idx] = mappingWithPreview;
    } else {
      updatedMappings.push(mappingWithPreview);
    }
  }

  return updatedMappings;
}

export function applySingleColumnMapping(
  newMapping: {targetField: PayrollTargetField; fileId: string; index: number},
  columnsByFileId: Record<string, ExtractedColumn[]>,
  setMappings: (
    updater: (mappings: PayrollFileColumnMapping[]) => PayrollFileColumnMapping[],
  ) => void,
  setSuggestions: (
    updater: (
      suggestions: Record<string, MappingSuggestion | undefined>,
    ) => Record<string, MappingSuggestion | undefined>,
  ) => void,
) {
  const column = columnsByFileId[newMapping.fileId]?.find((c) => c.index === newMapping.index);
  if (!column) {
    return;
  }

  const mapping: PayrollFileColumnMapping = {
    targetField: newMapping.targetField,
    columnTransformation: {
      type: 'NONE',
      column: {
        name: column.name,
        index: column.index,
        fileId: newMapping.fileId,
        pivotKeyValue: column.pivotKeyValue,
      },
    },
    valueTransformations: [],
    exampleValuePreTransformation: '',
    exampleValuePostTransformation: '',
  };

  const preview = calculateTransformationPreview(mapping, columnsByFileId);
  const mappingWithPreview = {
    ...mapping,
    exampleValuePreTransformation: preview.pre,
    exampleValuePostTransformation: preview.post,
  };

  setMappings((prev) => {
    const updatedMappings = [...prev];
    const idx = updatedMappings.findIndex((m) => m.targetField === newMapping.targetField);
    if (idx >= 0) {
      updatedMappings[idx] = mappingWithPreview;
    } else {
      updatedMappings.push(mappingWithPreview);
    }
    return updatedMappings;
  });

  setSuggestions((prev) => {
    const updatedSuggestions = {...prev};
    delete updatedSuggestions[newMapping.targetField];
    return updatedSuggestions;
  });
}

export function addPreviewToAdvancedChange(
  currentMappings: PayrollFileColumnMapping[],
  newMapping: Mapping,
  targetField: PayrollTargetField,
  extractedFiles: ExtractedFile[],
) {
  const idx = currentMappings.findIndex((m) => m.targetField === targetField);

  const preview = calculateTransformationPreviewFromExtracted(
    {
      columnTransformation: newMapping.columnTransformation,
      valueTransformations: newMapping.valueTransformations || [],
    },
    extractedFiles,
  );

  const merged: PayrollFileColumnMapping = {
    targetField,
    exampleValuePreTransformation: preview.pre,
    exampleValuePostTransformation: preview.post,
    columnTransformation: newMapping.columnTransformation,
    valueTransformations: newMapping.valueTransformations || [],
  };

  const out = [...currentMappings];
  if (idx >= 0) {
    out[idx] = merged;
  } else {
    out.push(merged);
  }
  return out;
}

export function getEmptyMapping(): Mapping {
  return {
    columnTransformation: {type: 'NONE', column: {name: '', index: 0, fileId: ''}},
    valueTransformations: [],
    exampleValuePreTransformation: '',
    exampleValuePostTransformation: '',
  };
}

export function applyDeductionsMapping(
  currentMappings: PayrollFileColumnMapping[],
  columns: PayrollFileColumnTransformationColumn[],
  targetField: PayrollTargetField,
  extractedFiles: ExtractedFile[],
) {
  const idx = currentMappings.findIndex((m) => m.targetField === targetField);
  const mapping: PayrollFileColumnMapping = {
    targetField,
    columnTransformation: {type: 'COLUMN_ADDITION', columns},
    valueTransformations: [],
    exampleValuePreTransformation: '',
    exampleValuePostTransformation: '',
  };
  const preview = calculateTransformationPreviewFromExtracted(
    {
      columnTransformation: mapping.columnTransformation,
      valueTransformations: mapping.valueTransformations,
    },
    extractedFiles,
  );
  const withPreview: PayrollFileColumnMapping = {
    ...mapping,
    exampleValuePreTransformation: preview.pre,
    exampleValuePostTransformation: preview.post,
  };
  const next = [...currentMappings];
  if (idx >= 0) {
    next[idx] = withPreview;
  } else {
    next.push(withPreview);
  }
  return next;
}

export function dedupePayrollColumns(cols: PayrollFileColumnTransformationColumn[]) {
  const seen = new Set<string>();
  const out: typeof cols = [];
  for (const c of cols) {
    const k = `${c.fileId}:${c.index}`;
    if (!seen.has(k)) {
      seen.add(k);
      out.push(c);
    }
  }
  return out;
}

export function getBaseColumnsFromMapping(
  mapping: Mapping,
): PayrollFileColumnTransformationColumn[] {
  if (!mapping.columnTransformation) return [];
  if (mapping.columnTransformation.type === 'NONE') {
    return [mapping.columnTransformation.column];
  }
  if (mapping.columnTransformation.type === 'PIVOT_COLUMN') {
    return [];
  }
  if (mapping.columnTransformation.type === 'STATIC_VALUE') {
    return [];
  }
  return mapping.columnTransformation.columns || [];
}

export function getTransformationColumnsFromMapping(
  mapping: PayrollFileColumnMapping,
): PayrollFileColumnTransformationColumn[] {
  if (!mapping.columnTransformation) return [];
  if (mapping.columnTransformation.type === 'NONE') {
    const column = mapping.columnTransformation.column;
    return [column, ...(column.fallbackColumns || [])];
  }
  if (mapping.columnTransformation.type === 'PIVOT_COLUMN') {
    // Return a pseudo-column reference with pivotKeyValue so validation skips it
    const pivotCol = mapping.columnTransformation.pivotColumn;
    return [
      {
        name: pivotCol.name,
        index: pivotCol.index,
        fileId: pivotCol.fileId,
        pivotKeyValue: pivotCol.pivotKeyValue,
      },
    ];
  }
  if (mapping.columnTransformation.type === 'STATIC_VALUE') {
    return [];
  }
  const cols = mapping.columnTransformation.columns || [];
  const flattened: PayrollFileColumnTransformationColumn[] = [];
  cols.forEach((col) => {
    flattened.push(col);
    if (col.fallbackColumns && col.fallbackColumns.length > 0) {
      flattened.push(...col.fallbackColumns);
    }
  });
  return flattened;
}
